import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button, Img, Heading, Text } from "../../components";
import { gsap } from "gsap/gsap-core";
import FooterSection from "components/FooterSection";
import Scene from "components/Background/ParticleShaders";
import state from "./state";
import useScrollPosition from "hooks/useScrollPosition";
import GsapSection1 from "components/Animation/GSAPSection1/GsapSection1";
import { ScrollTrigger } from "gsap/all";
import GsapSection3 from "components/Animation/GSAPSection1/GsapSection3";
import GsapSection1a from "components/Animation/GSAPSection1/GsapSection1a";
import TopMenuBar from "components/TopMenuBar";
import ScrollHint from "./ScrollHint";
import ProductDescription from "components/ProductDescription";
import useIsMobile from "hooks/useIsMobile";
import WhySection from "./WhySection";

gsap.registerPlugin(ScrollTrigger);

// Consistent section badge used across all sections
export function SectionBadge({ children }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      fontFamily: "Manrope, sans-serif",
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "rgba(255,255,255,0.55)",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: 20,
      padding: "5px 14px",
      background: "rgba(255,255,255,0.03)",
    }}>
      {children}
    </span>
  );
}

export default function AIHumanityPage() {
  const scrollY = useScrollPosition();
  const location = useLocation();
  const [showScrollHint, setShowScrollHint] = useState(true);
  let containerRef = useRef(null);
  const scrollArea = useRef();
  const isMobile = useIsMobile();
  const particleElement = useRef();

  const handleScroll = () => {
    state.top.current = scrollY;
    particleElement.current.zoomIn(state.top.current);
    setShowScrollHint(state.top.current <= 100);
  };

  useEffect(() => {
    handleScroll();
  }, [scrollY]);

  // Industries nav links land here as /#gaming, /#companions, /#robotics.
  // React Router doesn't trigger native hash-scroll on SPA navigation, so
  // scroll to the target section manually once it's mounted and laid out.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts < 20) {
        attempts += 1;
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  }, [location.hash]);

  return (
    <>
      <Helmet>
        <title>AIHumanity — The Relationship Layer for AI</title>
        <meta name="description" content="AIHumanity is the relationship layer for next-generation AI — turning intelligence into adaptive, persistent interaction for games, companions, assistants, and robots." />
      </Helmet>

      <div className="w-full sectionScroller" ref={scrollArea}>
        <main ref={(el) => (containerRef = el)} data-scroll-container className="main">

          {/* Fixed nav — takes 64px; sections need top-padding offset handled by pt-[64px] on hero */}
          <section style={{ position: "relative", zIndex: 100 }}>
            <TopMenuBar />
          </section>

          {/* ── Hero ──────────────────────────────────────────────── */}
          <section>
            <div
              style={{ height: "100vh", minHeight: 600, position: "relative" }}
              className="bg-black-900_02"
            >
              {/* Push content below fixed nav */}
              <Heading
                size="4xl"
                as="h1"
                className="w-[82%] md:w-[90%] sm:w-full h-max left-0 right-0 top-0 bottom-0 m-auto text-center absolute"
                style={{
                  lineHeight: "1.05",
                  letterSpacing: "-0.04em",
                  paddingTop: 64,
                  zIndex: 2,
                  textShadow: isMobile ? "0 12px 36px rgba(0,0,0,0.95)" : "none",
                  width: isMobile ? "92%" : undefined,
                }}
              >
                <span className="text-transparent bg-gradient5 bg-clip-text">
                  The Relationship Layer
                  <br />
                </span>
                <span className="text-white-A700">for Next-Generation AI</span>
              </Heading>

              {isMobile && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.42) 54%, rgba(0,0,0,0.7) 100%)",
                    pointerEvents: "none",
                  }}
                />
              )}

              <Scene
                ref={particleElement}
                style={isMobile ? { opacity: 0.34, filter: "blur(0.6px)" } : undefined}
              />

              {showScrollHint && <ScrollHint />}
            </div>
          </section>

          {/* ── Problem / Why ─────────────────────────────────────── */}
          <WhySection />

          {/* ── Market shift (stacked panels) ─────────────────────── */}
          <section>
            <GsapSection3 />
          </section>

          {/* ── Solution ──────────────────────────────────────────── */}
          {isMobile ? (
            <section style={{ position: "relative", zIndex: 10 }}>
              <GsapSection1 />
            </section>
          ) : (
            <section>
              <GsapSection1a />
            </section>
          )}

          {/* ── How It Works ──────────────────────────────────────── */}
          <section className="relative z-10 bg-black-900_02">
            {/* Tailwind-only padding — inline style would override md:/sm: classes */}
            <div className="flex flex-col gap-14 px-12 py-24 md:px-8 md:py-16 sm:px-5 sm:py-12 mx-auto" style={{ maxWidth: 1040 }}>

              <div className="flex flex-col gap-4">
                <SectionBadge>How It Works</SectionBadge>
                <Heading size="lg" as="h2" className="!text-gray-300 mt-2" style={{ maxWidth: 680 }}>
                  A three-layer runtime sitting between your model and your product — detecting
                  emotion and behavior, maintaining persistent relationship state, and adapting
                  tone, pacing, and strategy in real time.
                </Heading>
                <Text size="md" className="!text-white-A700_7f" style={{ maxWidth: 560 }}>
                  Model-agnostic. Embeddable in games, companion apps, assistants, and robots.
                </Text>
              </div>

              <div className="w-full mx-auto" style={{ maxWidth: 720 }}>
                <Img
                  src="images/system_layers.jpg"
                  alt="AIHumanity system layers diagram"
                  className="w-full h-auto rounded-xl block"
                />
              </div>
            </div>
          </section>

          {/* ── Use Cases (ProductDescription) ────────────────────── */}
          <section>
            <ProductDescription />
          </section>

          <FooterSection />
        </main>
      </div>
    </>
  );
}
